import React, { useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  Stack,
  Autocomplete,
  styled,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { renderAsync } from "docx-preview";
import {
  NavigateNext as NavigateNextIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../../contexts/ProfileContext";
import Cookies from "js-cookie";
import CircularProgress from "@mui/material/CircularProgress";
import axiosInstance from "../../../axios/axiosInstance";
import EducationCertifications from "./EducationsCertification";

// CSS for react-pdf to ensure it works nicely
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const UploadArea = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: "center",
  backgroundColor: theme.palette.background.default,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: 12,
  cursor: "pointer",
  minWidth: "300px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
  position: "relative",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(108, 92, 231, 0.04)",
  },
}));

const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const CareerDetails = () => {
  const navigate = useNavigate();
  const {
    profile,
    updateTempCareerDetails,
    tempProfileData,
    isProfileReadOnly,
    isEditingProfile,
    setIsEditingProfile,
    hasProfileChanges,
    setHasProfileChanges,
    hasExistingProfile,
    showFullProfileFields,
  } = useProfile();

  const [selectedFile, setSelectedFile] = useState(null);
  const [cvBase64, setCvBase64] = useState(null); // base64 string like data:application/pdf;base64,...
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [currentPosition, setCurrentPosition] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyValue, setCompanyValue] = useState(""); // final value
  const [loading, setLoading] = useState(false);
  const [educationList, setEducationList] = useState([]);
  const [certificationList, setCertificationList] = useState([]);
  const [employmentType, setEmploymentType] = useState([]);
  const [workSetup, setWorkSetup] = useState([]);
  const [willRelocate, setWillRelocate] = useState("No");
  const [workSetupOptions, setWorkSetupOptions] = useState([
    { id: 1, name: "On-site" },
    { id: 2, name: "Hybrid" },
    { id: 3, name: "Remote" },
  ]);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewContent, setViewContent] = useState(null);
  const [viewType, setViewType] = useState(null); // 'pdf' or 'docx'
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const token = Cookies.get("token");

  const decoded = jwtDecode(token);

  // helper to convert arraybuffer to base64
  const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  useEffect(() => {
    if (query.length < 1) return;

    const timeoutId = setTimeout(() => {
      const fetchCompanies = async () => {
        setLoading(true);
        try {
          const { data } = await axiosInstance.get(
            `/api/corporate/profile/corporate-list?q=${query
              .toLowerCase()
              .trim()}`,
          );
          setCompanies(data || []);
        } catch (err) {
          console.error("Error fetching companies:", err);
          setCompanies([]);
        } finally {
          setLoading(false);
        }
      };

      fetchCompanies();
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [query, token]);

  useEffect(() => {
    const fetchWorkSetup = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile/work-setup`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to fetch work setup");
        const data = await response.json();
        setWorkSetupOptions(data);
      } catch (error) {
        console.error("Error fetching work setup:", error);
      }
    };
    fetchWorkSetup();
  }, [token]);

  // Track local edits on this page to prevent useEffect from overwriting them
  const hasLocalEdits = useRef(false);

  // Initialize form data from temporary storage or existing profile
  useEffect(() => {
    // Don't overwrite local form state while user has made edits on this page
    if (isEditingProfile && hasLocalEdits.current) return;

    let dataSource = isProfileReadOnly
      ? profile?.careerDetails
      : tempProfileData.careerDetails || profile?.careerDetails;

    // Fallback to localStorage if necessary
    if (!dataSource) {
      try {
        const saved = localStorage.getItem("tempProfileData");
        if (saved) {
          const parsed = JSON.parse(saved);
          dataSource = parsed.careerDetails;
        }
      } catch (error) {
        console.error("Error loading from localStorage fallback:", error);
      }
    }

    if (dataSource) {
      setLinkedinUrl(dataSource.linkedin_url || "");
      setCurrentPosition(dataSource.current_position || "");
      setSelectedCompany(dataSource.selectedCompanyName || null);
      setCompanyValue(dataSource.companyName || "");
      setEducationList(dataSource.educations || []);
      setCertificationList(dataSource.certifications || []);
      setEmploymentType(dataSource.employmentType || []);
      setWorkSetup(dataSource.workSetup || []);
      setWillRelocate(dataSource.willRelocate || "No");

      // If temporary or saved data includes base64, use it
      if (dataSource.cv_file?.base64) {
        // Only overwrite if we don't have a local file with content
        if (!(selectedFile instanceof File && selectedFile.size > 0)) {
          setCvBase64(dataSource.cv_file.base64);
          // Also create a mock File object to show filename visually
          const mockFile = new File(
            [],
            dataSource.cv_file.name || dataSource.cv_filename || "cv.pdf",
            {
              type: dataSource.cv_file.type || "application/pdf",
            },
          );
          setSelectedFile(mockFile);
        }
      } else if (dataSource.cv_filename) {
        // We only have filename, set mock file for display, cvBase64 remains null,
        // so View will fetch from server when requested.
        if (!(selectedFile instanceof File && selectedFile.size > 0)) {
          const mockFile = new File([], dataSource.cv_filename, {
            type: "application/octet-stream",
          });
          setSelectedFile(mockFile);
        }
      } else {
        // Only clear if we don't have a local file with content
        if (!(selectedFile instanceof File && selectedFile.size > 0)) {
          setSelectedFile(null);
          setCvBase64(null);
        }
      }
    }
  }, [profile, tempProfileData.careerDetails, isProfileReadOnly]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    handleFile(file);
  };

  const handleFile = async (file) => {
    if (!file) return;

    // Allowed file extensions (primary validation)
    const allowedExtensions = ['.pdf', '.docx', '.doc'];
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
    ];

    const maxSize = 2 * 1024 * 1024; // 2 MB

    // Get file extension and validate
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    if (!allowedExtensions.includes(fileExtension)) {
      setError("Please upload a PDF or DOCX file");
      setSelectedFile(null);
      setCvBase64(null);
      return;
    }

    // Log if MIME type doesn't match but extension is valid (don't fail)
    if (file.type && !allowedTypes.includes(file.type) && file.size > 0) {
      console.warn(`File "${file.name}" has MIME type "${file.type}" but extension "${fileExtension}" is valid`);
    }

    if (file.size > maxSize) {
      setError("File size must be less than 2 MB");
      setSelectedFile(null);
      setCvBase64(null);
      return;
    }

    setError("");
    setSelectedFile(file);

    // Convert file to base64 immediately
    try {
      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });

      const base64 = await toBase64(file);
      setCvBase64(base64);

      // Mark as changed when editing
      if (isEditingProfile) {
        hasLocalEdits.current = true;
        setHasProfileChanges(true);
      }
    } catch (error) {
      console.error("Error converting file to base64:", error);
      setError("Failed to process file");
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    handleFile(file);
  };

  const handleLinkedinChange = (event) => {
    setLinkedinUrl(event.target.value);
    setError("");

    // Mark as changed when editing
    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  const handleCurrentPositionChange = (event) => {
    setCurrentPosition(event.target.value);
    setError("");

    // Mark as changed when editing
    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  const handleRemoveLinkedin = () => {
    setLinkedinUrl("");
    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  const validateLinkedInUrl = (url) => {
    if (!url) return true;
    const linkedInRegex =
      /^(https?:\/\/)?([\w]+\.)?linkedin\.com\/(in\/)?[\w\-\_\.]+\/?$/;
    return linkedInRegex.test(url);
  };

  const hasValidInput = () => {
    return selectedFile || (linkedinUrl && linkedinUrl.trim() !== "");
  };

  const handleSave = async () => {
    try {
      setError("");
      setSuccess("");

      if (!hasValidInput()) {
        setError("Please provide either a CV or LinkedIn profile URL");
        return;
      }

      if (linkedinUrl && !validateLinkedInUrl(linkedinUrl)) {
        setError("Please enter a valid LinkedIn profile URL");
        return;
      }

      // If selectedFile is a real File instance with content, build base64 now for temp storage
      let fileData = null;
      if (selectedFile instanceof File && selectedFile.size > 0) {
        // base64 should already be set from handleFile
        fileData = {
          name: selectedFile.name,
          type: selectedFile.type,
          base64: cvBase64,
        };
      } else if (cvBase64) {
        fileData = {
          name: selectedFile?.name || profile?.careerDetails?.cv_filename,
          type: "application/pdf",
          base64: cvBase64,
        };
      }

      const careerDetails = {
        linkedin_url: linkedinUrl || null,
        current_position: currentPosition || null,
        companyNameOrId: companyValue || null,
        selectedCompanyName: selectedCompany || null,
        cv_file: fileData,
        cv_filename: selectedFile?.name || null,
        educations: educationList,
        certifications: certificationList,
        employmentType: employmentType || [],
        workSetup: workSetup || [],
        willRelocate: willRelocate || "No",
      };

      await updateTempCareerDetails(careerDetails);
      setSuccess("Career details saved temporarily");
    } catch (error) {
      console.error("Error in handleSave:", error);
      setError(error.message || "Failed to save career details");
    }
  };

  const handleContinue = async () => {
    try {
      setError("");
      setSuccess("");

      if (!hasValidInput()) {
        setError("Please provide either a CV or LinkedIn profile URL");
        return;
      }

      if (showFullProfileFields) {
        if (!employmentType || employmentType.length === 0) {
          setError("Please select at least one employment type");
          return;
        }
        if (!workSetup || workSetup.length === 0) {
          setError("Please select at least one work setup option");
          return;
        }

        if (!educationList || educationList.length === 0) {
          setError("Please enter your education details");
          return;
        }
      }

      if (linkedinUrl && !validateLinkedInUrl(linkedinUrl)) {
        setError("Please enter a valid LinkedIn profile URL");
        return;
      }

      let fileData = null;

      if (selectedFile instanceof File && selectedFile.size > 0) {
        // base64 should already be set from handleFile
        fileData = {
          name: selectedFile.name,
          type: selectedFile.type,
          base64: cvBase64,
        };
      } else if (cvBase64) {
        fileData = {
          name: selectedFile?.name || profile?.careerDetails?.cv_filename,
          type: "application/pdf",
          base64: cvBase64,
        };
      }
      if (!companyValue && selectedCompany) {
        if (isEditingProfile) {
          hasLocalEdits.current = true;
          setHasProfileChanges(true);
        }
      }

      const careerDetails = {
        linkedin_url: linkedinUrl || null,
        current_position: currentPosition || null,
        companyNameOrId: companyValue
          ? companyValue
          : !companyValue && selectedCompany
            ? selectedCompany
            : null,
        selectedCompanyName: selectedCompany || null,
        cv_file: fileData,
        cv_filename: selectedFile?.name || null,
        educations: educationList,
        certifications: certificationList,
        employmentType: employmentType || [],
        workSetup: workSetup || [],
        willRelocate: willRelocate || "No",
      };

      await updateTempCareerDetails(careerDetails);
      navigate("/candidate/profile/preferred-roles");
    } catch (error) {
      console.error("Error saving before continue:", error);
      setError(error.message || "Failed to save career details");
    }
  };

  const handleCompanySelect = (event, newValue) => {
    const match = companies.find((c) => c.name === newValue);
    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }

    if (match) {
      setSelectedCompany(match.name);
      setCompanyValue(match.id);
    } else {
      setSelectedCompany(newValue);
      setCompanyValue("");
    }
  };

  // View CV. Uses local base64 if present, otherwise fetches from API by user id
  const handleViewCV = async () => {
    setError("");
    try {
      let base64ToView = cvBase64;

      // Derive file type from filename extension, NOT from MIME type (backend/browser often wrong)
      const filenameForType = (selectedFile?.name || profile?.careerDetails?.cv_filename || "").toLowerCase();
      let fileType = filenameForType.endsWith(".docx") || filenameForType.endsWith(".doc")
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : filenameForType.endsWith(".pdf")
          ? "application/pdf"
          : selectedFile?.type || "application/pdf";

      // If no base64 but we have a filename and profile id, fetch from server
      if (!base64ToView) {
        const filename =
          selectedFile?.name || profile?.careerDetails?.cv_filename || null;

        if (!filename) {
          setError("No CV available to view");
          return;
        }

        const userId = decoded?.id;
        if (!userId) {
          setError("Missing user id to fetch CV");
          return;
        }

        const res = await axiosInstance.get(`/api/profile/view-cv/${userId}`, {
          responseType: "arraybuffer",
        });

        const contentType = res.headers["content-type"] || "application/pdf";
        fileType = contentType;

        // ALWAYS trust filename extension over Content-Type (backend often returns wrong type)
        const filenameLower = filename.toLowerCase() || "";
        console.log("Filename extension check:", filenameLower);

        if (filenameLower.endsWith(".docx")) {
          console.log("Detected DOCX file by extension");
          base64ToView = res.data;
        } else if (filenameLower.endsWith(".pdf")) {
          console.log("Detected PDF file by extension");
          base64ToView = `data:${contentType};base64,${arrayBufferToBase64(res.data)}`;
          setCvBase64(base64ToView);
        } else if (contentType.includes("word") || contentType.includes("docx")) {
          console.log("Detected DOCX file by content-type");
          base64ToView = res.data;
        } else {
          console.log("No match, defaulting to PDF");
          base64ToView = `data:${contentType};base64,${arrayBufferToBase64(res.data)}`;
          setCvBase64(base64ToView);
        }
      }

      // Handle DOCX first (check filename extension, not just Content-Type)
      const filenameForCheck = (selectedFile?.name || profile?.careerDetails?.cv_filename || "").toLowerCase();

      if (
        filenameForCheck.endsWith(".docx") ||
        fileType.includes("word") ||
        fileType.includes("docx")
      ) {
        let arrayBuffer = base64ToView;

        if (typeof base64ToView === "string") {
          // Convert base64 back to array buffer for docx-preview
          const byteString = atob(base64ToView.split(",")[1]);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          arrayBuffer = ab;
        }

        console.log(
          "DOCX view content prepared. Type:",
          typeof arrayBuffer,
          "Length:",
          arrayBuffer?.byteLength,
        );
        setViewContent(arrayBuffer);
        setViewType("docx");
        setOpenViewDialog(true);
      }
      // Handle PDF
      else if (
        fileType.includes("pdf") ||
        (typeof base64ToView === "string" &&
          base64ToView.includes("application/pdf"))
      ) {
        // For react-pdf we can pass the base64 string directly or a blob
        setViewContent(base64ToView);
        setViewType("pdf");
        setOpenViewDialog(true);
      } else {
        setError("Unsupported file type for viewing.");
      }
    } catch (err) {
      console.error("CV View Error:", err);
      setError("Failed to load CV");
    }
  };

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  // Render DOCX when dialog opens and content is ready
  // Use a callback ref to trigger render when the element is mounted in the DOM
  const setDocxContainerRef = React.useCallback(
    (node) => {
      if (node && viewType === "docx" && viewContent) {
        const isBuffer =
          viewContent instanceof ArrayBuffer ||
          viewContent?.byteLength !== undefined;

        if (!isBuffer) {
          node.innerHTML = `<div style="padding: 20px;">Invalid document data format.</div>`;
          return;
        }

        node.innerHTML = `<div style="padding: 20px; color: #666;">Rendering document...</div>`;

        // Ensure we use a Blob
        const blob =
          viewContent instanceof Blob
            ? viewContent
            : new Blob([viewContent], {
              type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });

        renderAsync(blob, node, null, {
          className: "docx_viewer",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          debug: true,
        })
          .then(() => {
            console.log("docx-preview: renderAsync success");
            // Force a redraw or check if node is empty
            if (node.innerHTML === "") {
              node.innerHTML = `<div style="padding: 20px;">Document rendered but is empty.</div>`;
            }
          })
          .catch((err) => {
            node.innerHTML = `<div style="color: red; padding: 20px;">Error rendering document: ${err.message}</div>`;
          });
      }
    },
    [viewType, viewContent],
  );

  // Remove CV locally and in temporary storage
  const handleRemoveCV = async () => {
    if (isProfileReadOnly) return;

    setSelectedFile(null);
    setCvBase64(null);

    if (isEditingProfile) {
      hasLocalEdits.current = true;
      setHasProfileChanges(true);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, width: "100%", mx: "auto", mt: 0 }}>
      {/* Header with Edit Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Enter career details
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Please provide your CV or LinkedIn URL so that we can gain more
            insights. Either CV or LinkedIn URL must be provided.
          </Typography>
        </Box>

        {/* Show Edit Profile button only for users with existing profiles */}
        {hasExistingProfile && !isProfileReadOnly && !isEditingProfile && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setIsEditingProfile(true)}
            sx={{ minWidth: 160 }}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box elevation={3} sx={{ p: 4, mt: 4 }}>
        <Stack spacing={4}>
          {showFullProfileFields && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Current Company Name
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              What is your current company name?
            </Typography>

            <Autocomplete
              freeSolo
              fullWidth
              disabled={isProfileReadOnly || !isEditingProfile}
              value={selectedCompany}
              onChange={handleCompanySelect}
              onInputChange={(event, newInputValue) => {
                setQuery(newInputValue);
                setSelectedCompany(newInputValue);
              }}
              options={companies.map((company) => company.name)}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="e.g., Google, Microsoft, Amazon etc."
                  helperText="This is to ensure you don’t appear for your current employer."
                  InputProps={{
                    ...params.InputProps,
                    readOnly: isProfileReadOnly || !isEditingProfile,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
          )}

          {showFullProfileFields && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Current Position
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              What is your current job title or position?
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g., Senior Software Engineer, Marketing Manager, Data Analyst"
              value={currentPosition}
              onChange={handleCurrentPositionChange}
              helperText="This helps EQuAlly understand your current role and experience level."
              disabled={isProfileReadOnly || !isEditingProfile}
              InputProps={{ readOnly: isProfileReadOnly || !isEditingProfile }}
            />
          </Box>
          )}

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Upload your CV
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            ></Typography>
            <UploadArea
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              component="label"
              style={{
                pointerEvents:
                  isProfileReadOnly || !isEditingProfile ? "none" : "auto",
                opacity: isProfileReadOnly || !isEditingProfile ? 0.5 : 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main" }} />
                <Typography variant="body1">
                  {selectedFile
                    ? selectedFile.name
                    : "Click to upload or drag and drop"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PDF or Docx (Max 2MB)
                </Typography>
              </Box>

              <VisuallyHiddenInput
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileChange}
                disabled={isProfileReadOnly || !isEditingProfile}
              />

              {/* View and Remove buttons for CV */}
              <Box
                sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}
              >
                {selectedFile && (
                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewCV();
                    }}
                  >
                    View CV
                  </Button>
                )}
                {selectedFile && !isProfileReadOnly && isEditingProfile && (
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveCV();
                    }}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
            </UploadArea>

            {profile?.careerDetails?.cv_filename && !selectedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Current CV: {profile.careerDetails.cv_filename}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", my: 3 }}>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "divider" }} />
            <Typography
              variant="body2"
              sx={{
                px: 2,
                color: "text.secondary",
                fontWeight: "large",
              }}
            >
              OR
            </Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "divider" }} />
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              LinkedIn Profile URL
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            ></Typography>
            <TextField
              fullWidth
              placeholder="https://linkedin.com/in/your-profile"
              value={linkedinUrl}
              onChange={handleLinkedinChange}
              helperText="Add your LinkedIn profile URL if you'd like recruiters to view your profile"
              disabled={isProfileReadOnly || !isEditingProfile}
              InputProps={{
                readOnly: isProfileReadOnly || !isEditingProfile,
                endAdornment:
                  linkedinUrl && !isProfileReadOnly && isEditingProfile ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleRemoveLinkedin} edge="end">
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
              }}
            />
          </Box>

          {showFullProfileFields && (
          <>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Employment Type *
            </Typography>
            <FormControl component="fieldset">
              {["Full-time", "Part-time", "Contractor", "Freelancer"].map(
                (type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={employmentType.includes(type)}
                        onChange={(e) => {
                          if (isEditingProfile) {
                            hasLocalEdits.current = true;
                            setHasProfileChanges(true);
                          }
                          let updated = [...employmentType];
                          if (e.target.checked) {
                            updated.push(type);
                          } else {
                            updated = updated.filter((t) => t !== type);
                          }
                          setEmploymentType(updated);
                        }}
                        disabled={isProfileReadOnly || !isEditingProfile}
                      />
                    }
                    label={type}
                  />
                ),
              )}
            </FormControl>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Location Type *
            </Typography>
            <FormControl component="fieldset">
              {workSetupOptions.map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox
                      checked={workSetup.includes(option.id)}
                      onChange={(e) => {
                        if (isEditingProfile) {
                          hasLocalEdits.current = true;
                          setHasProfileChanges(true);
                        }
                        let updatedWorkSetup = [...workSetup];
                        if (e.target.checked) {
                          updatedWorkSetup.push(option.id);
                        } else {
                          updatedWorkSetup = updatedWorkSetup.filter(
                            (id) => id !== option.id,
                          );
                        }
                        setWorkSetup(updatedWorkSetup);
                      }}
                      disabled={isProfileReadOnly || !isEditingProfile}
                    />
                  }
                  label={option.name}
                />
              ))}
            </FormControl>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Are you willing to relocate?
            </Typography>
            <FormControl>
              <RadioGroup
                value={willRelocate}
                onChange={(e) => {
                  if (isEditingProfile) {
                    hasLocalEdits.current = true;
                    setHasProfileChanges(true);
                  }
                  setWillRelocate(e.target.value);
                }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio />}
                  label="Yes"
                  disabled={isProfileReadOnly || !isEditingProfile}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio />}
                  label="No"
                  disabled={isProfileReadOnly || !isEditingProfile}
                />
              </RadioGroup>
            </FormControl>
          </Box>

        <EducationCertifications
          educationList={educationList}
          setEducationList={setEducationList}
          certificationList={certificationList}
          setCertificationList={setCertificationList}
          disabled={isProfileReadOnly || !isEditingProfile}
          hasProfileChanges={hasProfileChanges}
          setHasProfileChanges={(val) => {
            if (isEditingProfile) {
              hasLocalEdits.current = true;
            }
            setHasProfileChanges(val);
          }}
        />
          </>
          )}
        </Stack>
      </Box>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {/* Show Cancel and Save buttons only for users with existing profiles */}
        {hasExistingProfile && isEditingProfile && (
          <>
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditingProfile(false);
                // Reload data from profile/tempProfileData
                const dataSource = isProfileReadOnly
                  ? profile?.careerDetails
                  : tempProfileData.careerDetails || profile?.careerDetails;
                if (dataSource) {
                  setLinkedinUrl(dataSource.linkedin_url || "");
                  setCurrentPosition(dataSource.current_position || "");
                  setSelectedCompany(dataSource.selectedCompanyName || null);
                  setCompanyValue(dataSource.companyName || "");
                  setEducationList(dataSource.educations || []);
                  setCertificationList(dataSource.certifications || []);
                  setEmploymentType(dataSource.employmentType || []);
                  setWorkSetup(dataSource.workSetup || []);
                  setWillRelocate(dataSource.willRelocate || "No");
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                await handleContinue();
              }}
            >
              Save & Continue
            </Button>
          </>
        )}
        {!hasExistingProfile && (
          <Button
            type="submit"
            variant="contained"
            size="large"
            endIcon={<NavigateNextIcon />}
            onClick={async () => {
              await handleContinue();
            }}
          >
            Continue
          </Button>
        )}
        {!isProfileReadOnly && !isEditingProfile && (
          <>
            <Button
              variant="contained"
              endIcon={<NavigateNextIcon />}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </>
        )}
      </Box>
      {/* CV View Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: "90vh" },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#6c5ce7",
            color: "white",
          }}
        >
          <Typography variant="h6">CV Preview</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpenViewDialog(false)}
            sx={{
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            height: "100%",
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          {viewType === "pdf" ? (
            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Document
                file={viewContent}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<CircularProgress />}
                error={
                  <Typography color="error">Failed to load PDF.</Typography>
                }
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={800} // adjustable width
                    className="mb-4 shadow-md"
                  />
                ))}
              </Document>
            </Box>
          ) : viewType === "docx" ? (
            <Box
              ref={setDocxContainerRef}
              sx={{
                p: 2,
                height: "100%",
                width: "100%",
                overflowY: "auto",
                bgcolor: "#fff",
                "& .docx-wrapper": {
                  background: "#fff !important",
                  backgroundColor: "#fff !important",
                  padding: "20px 0 !important",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                },
                "& section.docx": {
                  background: "#fff !important",
                  backgroundColor: "#fff !important",
                  boxShadow: "0 0 10px #fff !important",
                  margin: "0 auto 20px auto !important",
                },
              }}
            />
          ) : (
            <Typography sx={{ p: 4 }}>Unsupported file format</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default CareerDetails;
